// Imports for API functions and Redux Toolkit helpers
import {
  createTemplateStep1,
  uploadFileToMeta, // API function to create a WhatsApp template (step 1)
  whatsappBusinessNumbers, // API function to fetch WhatsApp business numbers
} from '../modules/channels/api/Api';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'; // Redux slice and async thunk helpers
import AspApi, { goApi, teamsServicePrefix } from 'api/ApiConfig'; // Custom Axios instance for API calls
import axios from 'axios'; // Axios for HTTP requests and error handling
import { updateCurrentBrandStatus } from './appSlice'; // Action to update brand status in app slice

import { MessageList } from 'constant/app/channel/tabl'; // Constant for table columns

// Async thunk to fetch Facebook connections for a given brand ID
// Parameters: brand_id
// Main logic: Make API call to fetch Facebook connections, handle errors, and return data
const fetchFacebookConnection = createAsyncThunk(
  'channel/connection',
  async (brand_id, { rejectWithValue }) => {
    try {
      // Make API call to fetch Facebook connections
      const res = await AspApi.get(
        `/api/v1/svc/facebook/connections?brand_id=${brand_id}`,
      );

      // Check if the response is an Axios error
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      // Return the data if successful
      return res?.data;
    } catch (error) {
      // Handle errors by rejecting with a message
      return rejectWithValue(error || 'Failed to fetch data');
    }
  },
);

// Async thunk to fetch Facebook pages list for a given brand ID
// Parameters: brand_id
// Main logic: Make API call to fetch Facebook pages, handle errors, and return data
const fetchFacebookPagesList = createAsyncThunk(
  'facebook/pages',
  async (brand_id, { rejectWithValue }) => {
    try {
      // Make API call to fetch Facebook pages
      const res = await AspApi.get(
        `/api/v1/svc/facebook/pages/list?brand_id=${brand_id}`,
      );

      // Check if the response is an Axios error
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      // Return the data if successful
      return res?.data;
    } catch (error) {
      // Handle errors by rejecting with a message
      return rejectWithValue(error || 'Failed to fetch data');
    }
  },
);

// Async thunk to fetch Facebook ad accounts list for a given brand ID
// Parameters: brand_id
// Main logic: Make API call to fetch Facebook ad accounts, handle errors, and return data
const fetchFacebookAdAccountsList = createAsyncThunk(
  'facebook/ad_accounts',
  async (brand_id, { rejectWithValue }) => {
    try {
      // Make API call to fetch Facebook ad accounts
      const res = await AspApi.get(
        `/api/v1/svc/facebook/ad_account/list?brand_id=${brand_id}`,
      );

      // Check if the response is an Axios error
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      // Return the data if successful
      return res?.data;
    } catch (error) {
      // Handle errors by rejecting with a message
      return rejectWithValue(error || 'Failed to fetch data');
    }
  },
);

// Async thunk to save selected Facebook page details
// Parameters: payload (page details)
// Main logic: Make API call to save Facebook page details, handle errors, and return data
const postSelectedPage = createAsyncThunk(
  'facebook/save_page_details',
  async (payload, { rejectWithValue }) => {
    try {
      // Make API call to save Facebook page details
      const res = await AspApi.post(`/api/v1/svc/facebook/pages`, payload);

      // Check if the response is an Axios error
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      // Return the data if successful
      return res?.data;
    } catch (error) {
      // Handle errors by rejecting with a message
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

// Async thunk to save selected Facebook ad account details
// Parameters: payload (ad account details)
// Main logic: Make API call to save Facebook ad account details, handle errors, and return data
const postSelectedAdAccount = createAsyncThunk(
  'facebook/save_ad_account_details',
  async (payload, { rejectWithValue }) => {
    try {
      // Make API call to save Facebook ad account details
      const res = await AspApi.post(`/api/v1/svc/facebook/ad_account`, payload);

      // Check if the response is an Axios error
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      // Return the data if successful
      return res?.data;
    } catch (error) {
      // Handle errors by rejecting with a message
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

// Async thunk to fetch WhatsApp business numbers for a given brand ID
// Parameters: brand_id
// Main logic: Make API call to fetch WhatsApp business numbers, handle errors, dispatch action to set brand ID, and return data
const fetchWhatsappNumbers = createAsyncThunk(
  'whatsapp/business_numbers',
  async (brand_id, { rejectWithValue, dispatch }) => {
    try {
      // Make API call to fetch WhatsApp business numbers
      const res = await whatsappBusinessNumbers(brand_id);

      // Check if the response is an Axios error
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      // Dispatch action to set the brand ID
      dispatch(setWabNumbersListBrandId(brand_id));
      // Return the data if successful
      return res?.data;
    } catch (error) {
      // Handle errors by rejecting with a message
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

// Async thunk to save a business phone number
// Parameters: payload (phone number details)
// Main logic: Make API call to save business phone number, handle errors, and return data
const saveBusinessPhoneNumber = createAsyncThunk(
  'whatsapp/save_business_numbers',
  async (payload, { rejectWithValue }) => {
    try {
      // Make API call to save business phone number
      const res = await AspApi.post(
        `/api/v1/svc/facebook/pages/page_business_number`,
        payload,
      );

      // Check if the response is an Axios error
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      // Return the data if successful
      return res?.data;
    } catch (error) {
      // Handle errors by rejecting with a message
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

// whatsapp business insights
const fetchWhatsappBusinessInsights = createAsyncThunk(
  'whatsapp/business_insights',
  async (payload, { rejectWithValue }) => {
    const { brand_id, start_date, end_date, phone_numbers, countries } =
      payload;

    try {
      const res = await AspApi.get(
        `/api/v1/svc/whatsapp/business_insights?brand_id=${brand_id}&start_date=${start_date}&end_date=${end_date}&phone_numbers=${phone_numbers.toString()}&countries=${countries.toString()}`,
        payload,
      );

      // axios is not raising an error when an error occurs
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      return res?.data;
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

const disconnectWhatsappChannel = createAsyncThunk(
  'whatsapp/disconnect',
  async (brand_id, { rejectWithValue, dispatch }) => {
    // const { brand_id, start_date, end_date, phone_numbers, countries } =
    // payload;

    try {
      const res = await AspApi.delete(
        `/api/v1/channels/disconnect_whatsapp?brand_id=${brand_id}`,
      );

      // axios is not raising an error when an error occurs
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      dispatch(updateCurrentBrandStatus(0));

      return res?.data;
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

// update business profile (this will be called in list phone numbers)
const updateWhatsappBusinessProfile = createAsyncThunk(
  'whatsapp/update_business_profile',
  async (
    { brand_id, phone_number_id, body, messaging_product },
    { rejectWithValue },
  ) => {
    const payload = {
      business_number_id: Number(phone_number_id),
      business_profile: { ...body, messaging_product },
    };
    try {
      const res = await AspApi.put(
        `/api/v1/svc/whatsapp/whatsapp_numbers/update_whatsapp_business_profile?brand_id=${brand_id}`,
        payload,
      );

      // axios is not raising an error when an error occurs
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      return { ...res?.data, phone_number_id };
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

const getWhatsappBusinessProfile = createAsyncThunk(
  'whatsapp/get_business_profile',
  async ({ brand_id, platform_name }, { rejectWithValue }) => {
    platform_name = platform_name || 'wapp';

    try {
      const res = await AspApi.get(
        `/api/v1/channels/channel_info?brand_id=${brand_id}&platform_name=${platform_name}`,
      );

      // axios is not raising an error when an error occurs
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      return res?.data;
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

const createTemplate = createAsyncThunk(
  'whatsapp/create_template',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await createTemplateStep1(payload);

      // axios is not raising an error when an error occurs
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      return res?.data;
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

const createTemplateFromLibrary = createAsyncThunk(
  'whatsapp/create_template_from_library',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await AspApi.post(
        `/api/v1/svc/whatsapp/template_utils/create_template_from_library`,
        payload,
      );

      // axios is not raising an error when an error occurs
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      return res?.data;
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

const getTemplatePerformance = createAsyncThunk(
  'whatsapp/get_template_performance',
  async (payload, { rejectWithValue }) => {
    const { brand_id, start_date, end_date } = payload;
    if (!start_date || !end_date) {
      return rejectWithValue('Invalid date range');
    }

    try {
      const res = await AspApi.get(
        `/api/v1/svc/whatsapp/business_insights/template_performance?brand_id=${brand_id}&start_date=${start_date}&end_date=${end_date}`,
      );

      // axios is not raising an error when an error occurs
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      return res?.data;
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

const metaAssetHandler = createAsyncThunk(
  'whatsapp/meta_asset_handler',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await uploadFileToMeta(payload);

      // axios is not raising an error when an error occurs
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      return res;
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

const fetchWebChatChannelList = createAsyncThunk(
  'webChat/fetch_webChat_channel_list',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await goApi.get(
        `${teamsServicePrefix}web_chat?brand_id=${payload.brand_id}`,
      );

      // axios is not raising an error when an error occurs
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      return res?.data?.data;
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

const getWebChatChannelData = createAsyncThunk(
  'webChat/get_webChat_channel_data',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await AspApi.get(
        `/api/v1/channels/web_chat_channel?channel=${payload.channel}&brand_id=${payload.brand_id}`,
      );

      // axios is not raising an error when an error occurs
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }
      console.log(res);

      return { data: res?.data?.data, channel_id: payload.channel };
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

const updateWebChatChannelData = createAsyncThunk(
  'webChat/update_webChat_channel_data',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await AspApi.put(
        `/api/v1/channels/update_web_chat_channel?channel=${payload.channel}&brand_id=${payload.brand_id}`,
        payload.data,
      );

      // axios is not raising an error when an error occurs
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      return { data: res?.data?.data, channel_id: payload.channel };
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

const createWebChatChannelData = createAsyncThunk(
  'webChat/create_webChat_channel_data',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await AspApi.post(
        `/api/v1/channels/create_web_chat_channel?brand_id=${payload.brand_id}`,
        payload.data,
      );

      // axios is not raising an error when an error occurs
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      return { data: res?.data?.data, channel_id: payload.channel };
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

const getUnassignedTeams = createAsyncThunk(
  'webChat/get_unassigned_teams',
  async ({ brand_id }, { rejectWithValue }) => {
    try {
      const res = await goApi.get(
        `${teamsServicePrefix}unassigned?brand_id=${brand_id}`,
      );

      // axios is not raising an error when an error occurs
      if (axios.isAxiosError(res)) {
        throw new Error(res.response?.data?.error);
      }

      return res?.data?.data;
    } catch (error) {
      return rejectWithValue(error || 'Failed to save data');
    }
  },
);

const initialState = {
  channelList: [],
  whatsUpAuthData: {},
  templateDetails: { category: '', name: '', language: [] },
  details: null,
  isSubmenuOpen: false,
  data: [],
  tableColumn: MessageList,

  // whatsapp business numbers
  wapNumbersListBrandId: null,
  whatsappBusinessNumbersList: [],
  whatsappBusinessNumbersListLoading: false,
  listingWhatsappBusinessNumbersListError: null,
  selectedPageWhatsappBusinessNumber: {},

  facebook_channel: {},
  facebook_connections_loading: false,
  fetch_facebook_connection_error: null,

  ad_accounts_list: [],
  facebook_ad_account_details: {},
  ad_accounts_list_loading: false,
  fetch_ac_accounts_error: null,
  save_ad_account_loading: false,

  pages_list: [],
  facebook_page_details: {},
  pages_list_loading: false,
  fetch_pages_error: null,
  page_business_number: null,
  save_page_loading: false, // For the post request

  saveWabaBusinessLoading: false,
  savingWabaBusinessError: null,

  // whatsapp business insights
  whatsappBusinessInsightsLoading: false,
  whatsappBusinessInsightsError: null,
  whatsappBusinessInsights: [],
  whatsappBusinessInsightsErrorCode: null,
  processedInsightsData: {
    allConversations: {
      total: 0,
      marketing: 0,
      utility: 0,
      authentication: 0,
      service: 0,
    },
    freeConversations: {
      total: 0,
      freeTier: 0,
      freeEntryPoint: 0,
    },
    paidConversations: {
      total: 0,
      marketing: 0,
      utility: 0,
      authentication: 0,
      service: 0,
    },
    charges: {
      total: 0,
      marketing: 0,
      utility: 0,
      authentication: 0,
      service: 0,
    },
  },

  phoneNumbersList: [],
  countryList: [
    { id: 1, name: 'India', value: 'IN', checked: false },
    { id: 2, name: 'USA', value: 'US', checked: false },
  ],

  // update business profile (use in phone numbers list in channels)
  updateBusinessProfileLoading: false,
  updateBusinessProfileError: null,

  // get business profile
  getBusinessProfileLoading: false,
  getBusinessProfileError: null,
  businessProfile: null,

  // template performance
  templatePerformanceList: [],
  templatePerformanceListCount: 0,
  templatePerformanceLoading: false,
  templatePerformanceError: null,

  // insights date filter
  dateRangeFilter: [],

  // meta asset handler
  metaAssetHandlerLoading: false,
  metaAssetHandlerError: null,
  // metaAssetHandlerResponse: null,

  // WebChat channel
  webChatChannelList: [],
  webChatChannelListLoading: false,
  webChatChannelListError: null,
  selectedWebChatChannel: {},

  // webChat channel data
  webChatChannelData: {},
  webChatChannelDataLoading: false,
  webChatChannelDataError: null,

  // update webChat channel
  updateWebChatChannelLoading: false,
  updateWebChatChannelError: null,

  // create webChat channel
  createWebChatChannelLoading: false,
  createWebChatChannelError: null,

  // unassigned teams
  unassignedTeams: [],
  unassignedTeamsLoading: false,
  unassignedTeamsError: null,
};

// process insights data for insights page
const processInsightsData = (insights, creditUsage) => {
  if (!insights || insights.length === 0)
    return initialState.processedInsightsData;

  const metrics = {
    allConversations: {
      total: 0,
      marketing: 0,
      utility: 0,
      authentication: 0,
      service: 0,
    },
    freeConversations: {
      total: 0,
      freeTier: 0,
      freeEntryPoint: 0,
    },
    paidConversations: {
      total: 0,
      marketing: 0,
      utility: 0,
      authentication: 0,
      service: 0,
    },
    charges: {
      total: 0,
      marketing: 0,
      utility: 0,
      authentication: 0,
      service: 0,
    },
  };

  insights.forEach((insight) => {
    const isRegular = insight.conversation_type === 'REGULAR';
    const category = insight.conversation_category.toLowerCase();

    // Update all conversations
    metrics.allConversations.total += insight.conversation;
    metrics.allConversations[category] += insight.conversation;

    // Update free/paid conversations
    if (isRegular) {
      metrics.paidConversations.total += insight.conversation;
      metrics.paidConversations[category] += insight.conversation;
      metrics.charges.total += insight.cost;
      metrics.charges[category] += insight.cost;
    } else {
      metrics.freeConversations.total += insight.conversation;
      if (insight.conversation_type === 'FREE_TIER') {
        metrics.freeConversations.freeTier += insight.conversation;
      } else if (insight.conversation_type === 'FREE_ENTRY_POINT') {
        metrics.freeConversations.freeEntryPoint += insight.conversation;
      }
    }
  });

  // Use creditUsage parameter if provided
  if (creditUsage) {
    metrics.charges.marketing += parseFloat(
      creditUsage.total_marketing_amount || 0,
    );
    metrics.charges.utility += parseFloat(
      creditUsage.total_utility_amount || 0,
    );
    metrics.charges.authentication += parseFloat(
      creditUsage.total_authentication_amount || 0,
    );
    metrics.charges.service += parseFloat(
      creditUsage.total_service_amount || 0,
    );
    metrics.charges.total =
      metrics.charges.marketing +
      metrics.charges.utility +
      metrics.charges.authentication +
      metrics.charges.service;
  }

  return metrics;
};

export const slice = createSlice({
  name: 'channel',
  initialState,
  reducers: {
    updateChannelList: (state, action) => {
      state.channelList = action.payload;
    },
    updateWhatsUpData: (state, action) => {
      state.whatsUpAuthData = action.payload;
    },
    updateTemplateDetails: (state, action) => {
      state.templateDetails = action.payload;
    },
    setData: (state, action) => {
      state.data = action.payload;
    },
    toggleSubmenu: (state) => {
      state.isSubmenuOpen = !state.isSubmenuOpen;
    },
    updateTabColumn: (state, action) => {
      state.tableColumn = action.payload;
    },
    flushFacebookChannel: (state, action) => {
      state.facebook_ad_account_details = {};
      state.facebook_page_details = {};
      state.page_business_number = null;
    },
    setFacebookChannel: (state, action) => {
      state.facebook_channel = action.payload;
    },
    setWhatsappBusinessNumbersList: (state, action) => {
      state.phoneNumbersList = state.phoneNumbersList.map((item) =>
        item.id === action.payload ? { ...item, checked: !item.checked } : item,
      );
    },
    clearWhatsappBusinessNumbersList: (state) => {
      state.phoneNumbersList = state.phoneNumbersList.map((item) => ({
        ...item,
        checked: false,
      }));
    },
    setCountryList: (state, action) => {
      state.countryList = state.countryList.map((item) =>
        item.id === action.payload ? { ...item, checked: !item.checked } : item,
      );
    },
    clearSelectedCountryList: (state) => {
      state.countryList = state.countryList.map((item) => ({
        ...item,
        checked: false,
      }));
    },
    flushWhatsappBusinessNumbersList: (state) => {
      state.whatsappBusinessNumbersList = [];
    },
    setWabNumbersListBrandId: (state, action) => {
      state.wapNumbersListBrandId = action.payload;
    },
    setDateRangeFilter: (state, action) => {
      state.dateRangeFilter = action.payload || [];
    },

    updateWebChatChannel: (state, action) => {
      state.webChatChannelData = {
        ...state.webChatChannelData,
        ...action.payload,
      };
    },

    clearWebChatChannelData: (state) => {
      state.webChatChannelData = {};
      state.selectedWebChatChannel = {};
    },

    setDefaultsForNewWebchatChannel: (state) => {
      state.webChatChannelData['details'] = {
        bot_name: '',
        logo: 'https://storage.googleapis.com/v1-assets-preprod-gainwix/assets/gainwix-images/Gainwix_website_assets/GainWix.svg',
        position: 'right',
        theme_color:
          'linear-gradient(white, white) padding-box, linear-gradient(to right, #0acd95, #428ce8, #6940f2) border-box',
        welcome_message: 'Hello, how can I help you?',
        bot_name: 'Gainwix',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchFacebookConnection
      .addCase(fetchFacebookConnection.pending, (state) => {
        state.facebook_connections_loading = true;
        state.fetch_facebook_connection_error = null;
        state.facebook_channel = {};
      })
      .addCase(fetchFacebookConnection.fulfilled, (state, action) => {
        state.facebook_connections_loading = false;
        const { channel_active, channel_id, facebook_connected, ...rest } =
          action.payload;

        state.facebook_channel = {
          channel_active,
          channel_id,
          facebook_connected,
        };
        state.facebook_page_details = rest.page_details || {};
        state.facebook_ad_account_details = rest.ad_account_details || {};
        state.page_business_number = rest.page_business_number || null;
      })
      .addCase(fetchFacebookConnection.rejected, (state, action) => {
        state.facebook_connections_loading = false;

        state.fetch_facebook_connection_error = action.payload.message;
        state.facebook_channel = {};
      })

      //fetchFacebookPagesList
      .addCase(fetchFacebookPagesList.pending, (state) => {
        state.pages_list_loading = true;
        state.fetch_pages_error = null;
        state.pages_list = [];
      })
      .addCase(fetchFacebookPagesList.fulfilled, (state, action) => {
        const { pages, selected_page } = action.payload;
        state.pages_list_loading = false;
        state.pages_list = pages;
        state.facebook_page_details = selected_page || {};
      })
      .addCase(fetchFacebookPagesList.rejected, (state, action) => {
        state.pages_list_loading = false;
        state.fetch_pages_error = action.payload.message;
        state.pages_list = [];
      })

      // fetchFacebookAdAccountsList
      .addCase(fetchFacebookAdAccountsList.pending, (state) => {
        state.ad_accounts_list_loading = true;
        state.fetch_ac_accounts_error = null;
        state.ad_accounts_list = [];
      })
      .addCase(fetchFacebookAdAccountsList.fulfilled, (state, action) => {
        const { selected_ad_account, ad_accounts } = action.payload;
        state.ad_accounts_list_loading = false;
        state.ad_accounts_list = ad_accounts;
        state.facebook_ad_account_details = selected_ad_account || {};
      })
      .addCase(fetchFacebookAdAccountsList.rejected, (state, action) => {
        state.ad_accounts_list_loading = false;
        state.fetch_ac_accounts_error = action.payload.message;
        state.ad_accounts_list = [];
      })

      .addCase(postSelectedPage.pending, (state, action) => {
        state.save_page_loading = true;
      })
      .addCase(postSelectedPage.fulfilled, (state, action) => {
        state.facebook_page_details = action.payload.page_details || {};
        state.save_page_loading = false;
      })
      .addCase(postSelectedPage.rejected, (state, action) => {
        state.save_page_loading = false;
      })

      .addCase(postSelectedAdAccount.pending, (state) => {
        state.save_ad_account_loading = true;
      })
      .addCase(postSelectedAdAccount.fulfilled, (state, action) => {
        state.facebook_ad_account_details =
          action.payload.ad_account_details || {};
        state.save_ad_account_loading = false;
      })
      .addCase(postSelectedAdAccount.rejected, (state) => {
        state.save_ad_account_loading = false;
      })

      .addCase(fetchWhatsappNumbers.pending, (state) => {
        state.whatsappBusinessNumbersListLoading = true;
        state.listingWhatsappBusinessNumbersListError = null;
      })
      .addCase(fetchWhatsappNumbers.fulfilled, (state, action) => {
        const { wapp_business_num, wapp_business_num_count } = action.payload;

        state.whatsappBusinessNumbersList = {
          wapp_business_num,
          wapp_business_num_count,
        };
        state.phoneNumbersList = wapp_business_num.map((item) => ({
          id: item.id,
          name: `${item.country_code} ${item.send_from_phone_number}`,
          value: Number(`${item.country_code}${item.send_from_phone_number}`),
          checked: false,
        }));
        state.whatsappBusinessNumbersListLoading = false;
      })
      .addCase(fetchWhatsappNumbers.rejected, (state, action) => {
        state.whatsappBusinessNumbersListLoading = false;
        state.whatsappBusinessNumbersList = [];
        state.listingWhatsappBusinessNumbersListError = action.payload.message;
      })

      .addCase(saveBusinessPhoneNumber.pending, (state) => {
        state.saveWabaBusinessLoading = true;
        state.savingWabaBusinessError = null;
      })
      .addCase(saveBusinessPhoneNumber.fulfilled, (state, action) => {
        state.page_business_number = action.payload.page_business_number;
        state.saveWabaBusinessLoading = false;
      })
      .addCase(saveBusinessPhoneNumber.rejected, (state, action) => {
        state.saveWabaBusinessLoading = false;
        state.savingWabaBusinessError = action.payload.message;
      })

      // ############# ######### #############
      // whatsapp cases
      // ############# ######### #############

      // fetchWhatsappBusinessInsights
      .addCase(fetchWhatsappBusinessInsights.pending, (state) => {
        state.whatsappBusinessInsightsLoading = true;
        state.whatsappBusinessInsights = [];
        state.whatsappBusinessInsightsError = null;
      })
      .addCase(fetchWhatsappBusinessInsights.fulfilled, (state, action) => {
        state.whatsappBusinessInsightsLoading = false;
        if (action.payload.sub_code == -1) {
          // state.whatsappBusinessInsightsErrorCode = action.payload.errors;
        } else if (action.payload.sub_code == 10) {
          state.whatsappBusinessInsights = action.payload.insights;
          state.processedInsightsData = processInsightsData(
            action.payload.insights,
            action.payload.credit_usage_insights,
          );
        }
      })
      .addCase(fetchWhatsappBusinessInsights.rejected, (state, action) => {
        state.whatsappBusinessInsightsLoading = false;
        // state.whatsappBusinessInsightsError = action.payload.errors;
        state.whatsappBusinessInsightsErrorCode = action.payload;
      })

      // update business profile
      .addCase(updateWhatsappBusinessProfile.pending, (state) => {
        state.updateBusinessProfileLoading = true;
        state.updateBusinessProfileError = null;
      })
      .addCase(updateWhatsappBusinessProfile.fulfilled, (state, action) => {
        state.updateBusinessProfileLoading = false;
        state.updateBusinessProfileError = null;
        const { phone_number_id, profile_info } = action.payload;

        state.whatsappBusinessNumbersList?.wapp_business_num?.map((item) => {
          if (item.send_from_phone_number_id === phone_number_id) {
            item.profile_info = profile_info;
          }
        });
      })
      .addCase(updateWhatsappBusinessProfile.rejected, (state, action) => {
        state.updateBusinessProfileLoading = false;
        state.updateBusinessProfileError = action.payload;
      })

      // get business profile
      .addCase(getWhatsappBusinessProfile.pending, (state) => {
        state.getBusinessProfileLoading = true;
        state.getBusinessProfileError = null;
      })
      .addCase(getWhatsappBusinessProfile.fulfilled, (state, action) => {
        state.getBusinessProfileLoading = false;
        state.getBusinessProfileError = null;

        state.businessProfile = action.payload?.data;
      })
      .addCase(getWhatsappBusinessProfile.rejected, (state, action) => {
        state.getBusinessProfileLoading = false;
        state.getBusinessProfileError = action.payload;
      })

      // get template performance
      .addCase(getTemplatePerformance.pending, (state) => {
        state.templatePerformanceLoading = true;
        state.templatePerformanceError = null;
      })
      .addCase(getTemplatePerformance.fulfilled, (state, action) => {
        state.templatePerformanceLoading = false;
        state.templatePerformanceError = null;

        state.templatePerformanceList = action.payload?.data;
        state.templatePerformanceListCount = action.payload?.total;
      })
      .addCase(getTemplatePerformance.rejected, (state, action) => {
        state.templatePerformanceLoading = false;
        state.templatePerformanceError = action.payload;
      })

      // meta asset handler
      .addCase(metaAssetHandler.pending, (state) => {
        state.metaAssetHandlerLoading = true;
        state.metaAssetHandlerError = null;
      })
      .addCase(metaAssetHandler.fulfilled, (state, action) => {
        state.metaAssetHandlerLoading = false;
        state.metaAssetHandlerError = null;

        // state.metaAssetHandlerResponse = action.payload;
      })
      .addCase(metaAssetHandler.rejected, (state, action) => {
        state.metaAssetHandlerLoading = false;
        state.metaAssetHandlerError = action.payload;
      })

      // webChat
      .addCase(fetchWebChatChannelList.pending, (state) => {
        state.webChatChannelListLoading = true;
        state.webChatChannelListError = null;
      })
      .addCase(fetchWebChatChannelList.fulfilled, (state, action) => {
        state.webChatChannelListLoading = false;
        state.webChatChannelListError = null;

        state.webChatChannelList = action.payload || [];
      })
      .addCase(fetchWebChatChannelList.rejected, (state, action) => {
        state.webChatChannelListLoading = false;
        state.webChatChannelListError = action.payload;
      })
      .addCase(getWebChatChannelData.pending, (state) => {
        state.webChatChannelDataLoading = true;
        state.webChatChannelDataError = null;
      })
      .addCase(getWebChatChannelData.fulfilled, (state, action) => {
        state.webChatChannelDataLoading = false;
        state.webChatChannelDataError = null;
        console.log(action.payload);

        state.selectedWebChatChannel = state.webChatChannelList.find(
          (item) => item.id === action.payload.channel_id,
        );
        state.webChatChannelData = action.payload.data || {};
      })
      .addCase(getWebChatChannelData.rejected, (state, action) => {
        state.webChatChannelDataLoading = false;
        state.webChatChannelDataError = action.payload;
      })
      .addCase(createWebChatChannelData.pending, (state) => {
        state.webChatChannelDataLoading = true;
        state.webChatChannelDataError = null;
      })
      .addCase(createWebChatChannelData.fulfilled, (state, action) => {
        state.webChatChannelDataLoading = false;
        state.webChatChannelDataError = null;
        state.selectedWebChatChannel = state.webChatChannelList.find(
          (item) => item.id === action.payload.channel_id,
        );
        state.webChatChannelData = action.payload.data || {};
      })
      .addCase(createWebChatChannelData.rejected, (state, action) => {
        state.webChatChannelDataLoading = false;
        state.webChatChannelDataError = action.payload;
      })
      .addCase(updateWebChatChannelData.pending, (state) => {
        state.webChatChannelDataLoading = true;
        state.webChatChannelDataError = null;
      })
      .addCase(updateWebChatChannelData.fulfilled, (state, action) => {
        state.webChatChannelDataLoading = false;
        state.webChatChannelDataError = null;
        state.selectedWebChatChannel = state.webChatChannelList.find(
          (item) => item.id === action.payload.channel_id,
        );
        state.webChatChannelData =
          { ...action.payload.data, id: action.payload.data.uuid } ||
          state.webChatChannelData ||
          {};
      })
      .addCase(updateWebChatChannelData.rejected, (state, action) => {
        state.webChatChannelDataLoading = false;
        state.webChatChannelDataError = action.payload;
      })
      .addCase(getUnassignedTeams.pending, (state) => {
        state.unassignedTeamsLoading = true;
        state.unassignedTeamsError = null;
      })
      .addCase(getUnassignedTeams.fulfilled, (state, action) => {
        state.unassignedTeamsLoading = false;
        state.unassignedTeamsError = null;
        state.unassignedTeams = action.payload || [];
      })
      .addCase(getUnassignedTeams.rejected, (state, action) => {
        state.unassignedTeamsLoading = false;
        state.unassignedTeamsError = action.payload;
      });
  },
});

export const {
  update,
  updateLanguage,
  updateChannelList,
  updateWhatsUpData,
  updateTemplateDetails,
  setData,
  updateTabColumn,
  toggleSubmenu,
  flushFacebookChannel,
  setFacebookChannel,
  setWhatsappBusinessNumbersList,
  clearWhatsappBusinessNumbersList,
  flushWhatsappBusinessNumbersList,
  setCountryList,
  clearSelectedCountryList,
  setWabNumbersListBrandId,
  setDateRangeFilter,
  updateWebChatChannel,
  clearWebChatChannelData,
  setDefaultsForNewWebchatChannel,
} = slice.actions;

export {
  fetchFacebookConnection,
  fetchFacebookPagesList,
  fetchFacebookAdAccountsList,
  postSelectedPage,
  postSelectedAdAccount,
  fetchWhatsappNumbers,
  saveBusinessPhoneNumber,
  fetchWhatsappBusinessInsights,
  disconnectWhatsappChannel,
  updateWhatsappBusinessProfile,
  getWhatsappBusinessProfile,
  createTemplate,
  createTemplateFromLibrary,
  getTemplatePerformance,
  metaAssetHandler,

  // webChat
  fetchWebChatChannelList,
  getWebChatChannelData,
  createWebChatChannelData,
  updateWebChatChannelData,
  getUnassignedTeams,
};

export const selectProcessedInsightsData = (state) =>
  state.Channel.processedInsightsData;
export const selectWhatsappBusinessInsights = (state) =>
  state.Channel.whatsappBusinessInsights;
export const selectWhatsappBusinessInsightsLoading = (state) =>
  state.Channel.whatsappBusinessInsightsLoading;

export const selectUpdateBusinessProfileLoading = (state) =>
  state.Channel.updateBusinessProfileLoading;
export const selectUpdateBusinessProfileError = (state) =>
  state.Channel.updateBusinessProfileError;

export default slice.reducer;
